import React, { useState } from 'react';
import { Briefcase, Upload, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import Seo from '../components/Seo';

const Careers = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    yearsExperience: '',
    availability: '',
    cprCertified: false,
    wheelchairExperience: false,
    backgroundConsent: false,
    additionalInfo: ''
  });

  const [licenseFile, setLicenseFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { ref } on success

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const LICENSE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
  const LICENSE_EXTS = /\.(pdf|jpg|jpeg|png)$/i;
  const RESUME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const RESUME_EXTS = /\.(pdf|doc|docx)$/i;
  const MAX_BYTES = 10 * 1024 * 1024;

  const validateFile = (file, allowedTypes, allowedExtsRe, label) => {
    if (!file) return `${label} is required`;
    if (file.size === 0) return `${label} file is empty`;
    if (file.size > MAX_BYTES) return `${label} exceeds the 10 MB limit`;
    const typeOk = allowedTypes.includes(file.type);
    const extOk = allowedExtsRe.test(file.name || '');
    if (!typeOk && !extOk) return `${label} must be ${label === 'Résumé' ? 'PDF, DOC, or DOCX' : 'PDF, JPG, or PNG'}`;
    return null;
  };

  const handleLicenseChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const err = validateFile(file, LICENSE_TYPES, LICENSE_EXTS, "Driver's License");
    if (err) { toast.error(err); e.target.value = ''; return; }
    setLicenseFile(file);
    toast.success(`Driver's license attached: ${file.name}`);
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const err = validateFile(file, RESUME_TYPES, RESUME_EXTS, 'Résumé');
    if (err) { toast.error(err); e.target.value = ''; return; }
    setResumeFile(file);
    toast.success(`Résumé attached: ${file.name}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName || !formData.phone || !formData.email || !formData.yearsExperience) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.backgroundConsent) {
      toast.error('Background check consent is required');
      return;
    }
    const licenseErr = validateFile(licenseFile, LICENSE_TYPES, LICENSE_EXTS, "Driver's License");
    if (licenseErr) { toast.error(licenseErr); return; }
    const resumeErr = validateFile(resumeFile, RESUME_TYPES, RESUME_EXTS, 'Résumé');
    if (resumeErr) { toast.error(resumeErr); return; }

    setSubmitting(true);
    try {
      const body = new FormData();
      body.append('full_name', formData.fullName);
      body.append('phone', formData.phone);
      body.append('email', formData.email);
      body.append('years_experience', formData.yearsExperience);
      body.append('availability', formData.availability || '');
      body.append('position', 'Wheelchair Van Driver');
      body.append('cpr_certified', String(!!formData.cprCertified));
      body.append('wheelchair_experience', String(!!formData.wheelchairExperience));
      body.append('background_consent', 'true');
      body.append('additional_info', formData.additionalInfo || '');
      body.append('license_file', licenseFile);
      body.append('resume_file', resumeFile);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/careers/driver-application`, {
        method: 'POST',
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Application could not be saved. Please try again.');
        return;
      }
      toast.success(`Application received (ref ${data.ref}). We will contact you within 3-5 business days.`);
      setSubmitted({ ref: data.ref });
      setFormData({
        fullName: '', phone: '', email: '', yearsExperience: '', availability: '',
        cprCertified: false, wheelchairExperience: false, backgroundConsent: false, additionalInfo: '',
      });
      setLicenseFile(null);
      setResumeFile(null);
    } catch (err) {
      toast.error('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    "Competitive pay with performance bonuses",
    "Flexible scheduling options",
    "Paid training and certifications",
    "Health insurance options",
    "Professional development opportunities",
    "Supportive team environment"
  ];

  const requirements = [
    "Valid Texas driver's license",
    "Clean driving record (3+ years)",
    "Pass background check and drug screening",
    "Ability to lift up to 50 lbs",
    "Professional appearance and demeanor",
    "Excellent customer service skills"
  ];

  return (
    <div className="min-h-screen pt-20">
      <Seo
        title="Careers | Medical Transportation Drivers Houston | MoveraCare Mobility"
        description="Join MoveraCare Mobility as a professional non-emergency medical transportation driver serving patients and healthcare facilities across Greater Houston."
        path="/careers"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Careers', url: '/careers' }]}
      />
      {/* Hero Section */}
      <section className="py-20 bg-[#004C97]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Briefcase className="w-16 h-16 text-[#F9A825] mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Join Our Team</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Become a professional medical transportation driver with MoveraCare Mobility
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Why Work With Us */}
        <section className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Why Work With Us?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join a professional team making a difference in healthcare transportation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-[#F9A825] flex-shrink-0 mt-1" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Application Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Driver Application Form</CardTitle>
                <CardDescription>
                  Complete the application below to join our team of professional medical transportation drivers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-[#004C97]">Personal Information</h3>
                    
                    <div>
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        value={formData.fullName}
                        onChange={(e) => handleChange('fullName', e.target.value)}
                        placeholder="Your full legal name"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="(123) 456-7890"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="your.email@example.com"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Experience */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-[#004C97]">Experience</h3>
                    
                    <div>
                      <Label htmlFor="yearsExperience">Years of Driving Experience *</Label>
                      <Select value={formData.yearsExperience} onValueChange={(value) => handleChange('yearsExperience', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select years of experience" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3-5">3-5 years</SelectItem>
                          <SelectItem value="5-10">5-10 years</SelectItem>
                          <SelectItem value="10+">10+ years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-[#004C97]">Certifications & Skills</h3>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="cprCertified"
                        checked={formData.cprCertified}
                        onCheckedChange={(checked) => handleChange('cprCertified', checked)}
                      />
                      <Label htmlFor="cprCertified" className="cursor-pointer">
                        I am CPR certified
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="wheelchairExperience"
                        checked={formData.wheelchairExperience}
                        onCheckedChange={(checked) => handleChange('wheelchairExperience', checked)}
                      />
                      <Label htmlFor="wheelchairExperience" className="cursor-pointer">
                        I have wheelchair securement experience
                      </Label>
                    </div>
                  </div>

                  {/* Driver License Upload */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-[#004C97]">Driver's License *</h3>
                    <div>
                      <Label htmlFor="license">Upload Driver's License</Label>
                      <div className="mt-2">
                        <label htmlFor="license" className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#F9A825] transition-colors" data-testid="careers-license-dropzone">
                          <div className="text-center">
                            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <span className="text-sm text-gray-600">
                              {licenseFile ? licenseFile.name : "Click to upload driver's license"}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">PDF, JPG, or PNG · max 10 MB</p>
                          </div>
                        </label>
                        <input
                          id="license"
                          data-testid="careers-license-input"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                          onChange={handleLicenseChange}
                          className="hidden"
                        />
                        {licenseFile && (
                          <p className="text-xs text-emerald-700 mt-2" data-testid="careers-license-confirm">
                            ✓ {licenseFile.name} ({Math.round(licenseFile.size / 1024)} KB) attached
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Résumé Upload */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-[#004C97]">Résumé *</h3>
                    <div>
                      <Label htmlFor="resume">Upload Résumé</Label>
                      <div className="mt-2">
                        <label htmlFor="resume" className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#F9A825] transition-colors" data-testid="careers-resume-dropzone">
                          <div className="text-center">
                            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <span className="text-sm text-gray-600">
                              {resumeFile ? resumeFile.name : 'Click to upload résumé'}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">PDF, DOC, or DOCX · max 10 MB</p>
                          </div>
                        </label>
                        <input
                          id="resume"
                          data-testid="careers-resume-input"
                          type="file"
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={handleResumeChange}
                          className="hidden"
                        />
                        {resumeFile && (
                          <p className="text-xs text-emerald-700 mt-2" data-testid="careers-resume-confirm">
                            ✓ {resumeFile.name} ({Math.round(resumeFile.size / 1024)} KB) attached
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <Label htmlFor="additionalInfo">Additional Information</Label>
                    <Textarea
                      id="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={(e) => handleChange('additionalInfo', e.target.value)}
                      placeholder="Tell us why you'd be a great fit for MoveraCare Mobility"
                      rows={4}
                    />
                  </div>

                  {/* Background Check Consent */}
                  <div className="bg-[#F9A825]/10 border border-[#F9A825]/30 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="backgroundConsent"
                        checked={formData.backgroundConsent}
                        onCheckedChange={(checked) => handleChange('backgroundConsent', checked)}
                        className="mt-1"
                      />
                      <Label htmlFor="backgroundConsent" className="cursor-pointer text-sm">
                        I consent to a background check and drug screening as part of the employment process. I understand this is required for all driver positions at MoveraCare Mobility. *
                      </Label>
                    </div>
                  </div>

                  <Button type="submit" size="lg" disabled={submitting} data-testid="careers-submit-btn" className="w-full bg-[#F9A825] text-white hover:bg-[#E08E1E] font-semibold disabled:opacity-60">
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </Button>
                  {submitted && (
                    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="careers-success">
                      Application saved successfully. Reference <strong>{submitted.ref}</strong> — we'll be in touch within 3-5 business days.
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="border-2 border-[#F9A825]">
              <CardHeader>
                <CardTitle>Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#004C97] text-white">
              <CardHeader>
                <CardTitle>Application Process</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#F9A825] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">1</div>
                  <p className="text-sm text-gray-200">Submit application online</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#F9A825] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">2</div>
                  <p className="text-sm text-gray-200">Phone screening interview</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#F9A825] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">3</div>
                  <p className="text-sm text-gray-200">Background check & drug test</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#F9A825] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">4</div>
                  <p className="text-sm text-gray-200">In-person interview & training</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Questions?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">Contact our dispatch team:</p>
                <a href="mailto:dispatch@moveracaremobility.com" className="text-[#004C97] hover:text-[#F9A825] transition-colors font-medium">
                  dispatch@moveracaremobility.com
                </a>
                <p className="text-sm text-gray-600 mt-4">
                  Or call: <a href="tel:+18329252128" className="text-[#004C97] hover:text-[#F9A825] transition-colors font-medium">(832) 925-2128</a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;
